// ============================================================
//  PAYMENTS PAGE — ADMIN DASHBOARD
//  Path: src/pages/PaymentsPage.tsx
//
//  The financial rollup view — three numbers that always go
//  together: what customers paid → our cut → what taskers
//  earned. Complements TasksPage (operational, per-job) with a
//  finance-oriented summary.
//
//  Access: super_admin, admin only.
// ============================================================

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getPaymentsSummary } from '../services/admin.service'
import { PaymentsSummary } from '../types'

const PaymentsPage: React.FC = () => {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<PaymentsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  // FIX: a failed load used to be console-only, so the page rendered
  // $0.00 on every card, "0 paid transactions" and "No transactions
  // yet" — on the finance page, that reads as real (and alarming) data.
  // A failure now shows an error with a retry, and nothing shows a
  // figure the backend didn't actually send.
  const [error, setError]     = useState('')

  const loadSummary = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await getPaymentsSummary()
      setSummary(res.data.data)
    } catch (err: any) {
      console.error('Failed to fetch payments summary:', (err as any)?.response?.status, (err as any)?.message)
      setSummary(null)
      setError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load payments.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadSummary() }, [])

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-400">Loading...</div></div>
  }

  if (error || !summary) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Payments & Earnings</h1>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{error || 'No payment data was returned.'}</p>
          <button onClick={() => loadSummary()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      </div>
    )
  }

  // value is null when the backend didn't send that figure — shown as "—"
  // instead of a made-up $0.00.
  const cards: { label: string; value: number | null; icon: string; color: string; desc: string }[] = [
    { label: 'Transaction Volume', value: summary?.totalRevenue ?? 0,       icon: '💳', color: 'bg-green-600/20 text-green-400',  desc: 'Customer Paid — gross amount customers were charged (captured), not platform revenue' },
    // FIX: these read "the 20% fee" and "80% — what taskers earned".
    // The flat 20/80 split was removed from the backend — commission is
    // resolved per task through resolvePlatformFeePercent, which honours
    // Category.commissionExempt (0%) and Category.commissionPercent
    // (anything the admin sets). A commission-exempt category pays the
    // platform nothing, so a label promising 20% is simply wrong, and
    // it is wrong on the one screen someone would check to find that out.
    //
    // Also note this figure is COMMISSION ONLY — service fees, insurance
    // fees and tax are collected from the customer too and are not in
    // here. See the note on splitTaskRevenue in admin.service.js.
    // Platform Revenue is the platform's REAL take: the whole application
    // fee Stripe sends to MatchnBook (commission + service + insurance fees
    // + the tax collected on them). Tax is inside it and shown separately.
    { label: 'Platform Revenue', value: summary?.totalPlatformRevenue ?? summary?.totalPlatformFees ?? 0, icon: '🏦', color: 'bg-brand-600/20 text-brand-400', desc: 'Everything MatchnBook received from Stripe (application fees), including tax' },
    { label: 'Tax Collected', value: summary?.totalTaxCollected ?? null, icon: '🧾', color: 'bg-orange-600/20 text-orange-400', desc: 'Inside Platform Revenue — to be remitted, not income' },
    { label: 'MatchnBook Keeps', value: summary?.totalPlatformKeeps ?? null, icon: '💰', color: 'bg-emerald-600/20 text-emerald-400', desc: 'Platform Revenue minus tax collected' },
    { label: 'Commission Only', value: summary?.totalPlatformFees ?? 0, icon: '📊', color: 'bg-gray-600/20 text-gray-400', desc: 'Commission part of the above — varies per category' },
    { label: 'Stripe Fees', value: summary?.totalStripeFees ?? null, icon: '🏧', color: 'bg-blue-600/20 text-blue-400', desc: "Taken from taskers' accounts. Exact once Stripe confirms, an estimate before" },
    // Adjustment: real Stripe fee minus the processing fee that was meant to cover it.
    { label: 'Stripe Fee Adjustment', value: summary?.totalStripeFeeAdjustment ?? null, icon: '⚖️', color: 'bg-yellow-600/20 text-yellow-400', desc: `Real Stripe fee minus the estimate (+ = Stripe took more). ${summary?.stripeFeesAwaitingExact ?? 0} payment(s) still waiting for the exact fee` },
    // Direct charges: the money goes straight into each tasker's own Stripe
    // account when the job is confirmed — the platform never holds it, and
    // Stripe's bank payouts follow each tasker's own schedule (not visible here).
    { label: 'Tasker Earnings',    value: summary?.totalExpertPayouts ?? 0, icon: '🧑‍🔧', color: 'bg-purple-600/20 text-purple-400', desc: "Taskers' net in their own Stripe accounts, after all fees (final once Stripe confirms)" },
    // Held, not charged. Needs totalOnHold from the backend — "—" until it is sent.
    { label: 'On Hold',            value: summary?.totalOnHold ?? null,     icon: '⏳', color: 'bg-yellow-600/20 text-yellow-400', desc: "Held on customers' cards for upcoming jobs — not charged yet" },
    // Instant payouts no longer exist (taskers manage payouts in their own
    // Stripe account). Only shown if old rows still carry a fee.
    ...((summary?.totalInstantFees ?? 0) > 0
      ? [{ label: 'Instant Payout Fees (legacy)', value: summary?.totalInstantFees ?? 0, icon: '⚡', color: 'bg-gray-600/20 text-gray-400', desc: 'From payouts made before the switch to Stripe Standard accounts' }]
      : []),
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Payments & Earnings</h1>
        <p className="text-gray-400 text-sm mt-1">{summary?.transactionCount ?? 0} paid transactions</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((card) => (
          <div key={card.label} className="bg-surface-card border border-border-light rounded-2xl p-5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${card.color}`}>
              <span className="text-lg">{card.icon}</span>
            </div>
            <p className="text-2xl font-bold text-white">{card.value != null ? `$${card.value.toFixed(2)}` : '—'}</p>
            <p className="text-sm text-gray-400 mt-1">{card.label}</p>
            <p className="text-xs text-gray-600 mt-0.5">{card.desc}</p>
          </div>
        ))}
      </div>

      {(summary?.totalRefundedGross ?? 0) > 0 && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 mb-6">
          <p className="text-sm text-red-400">
            <strong>${summary?.totalRefundedGross.toFixed(2)}</strong> refunded across all tasks (gross amount charged — not net of the exact refund percentage per cancellation tier).
          </p>
        </div>
      )}

      <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border-light">
          <h2 className="text-lg font-semibold text-white">Recent Transactions</h2>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Task</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Customer</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Base Price</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Insurance (C/T)</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Service Fee (C/T)</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Processing (C/T)</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Tax (customer / on fees)</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Customer Paid</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Stripe Took</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Tasker Took</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Platform Took</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Commission</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Hired</th>
            </tr>
          </thead>
          <tbody>
            {(summary?.recentTransactions.length ?? 0) === 0 ? (
              <tr><td colSpan={15} className="text-center py-8 text-gray-400">No transactions yet</td></tr>
            ) : (
              summary?.recentTransactions.map((t: any) => (
                <tr key={t.taskId} className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors">
                  <td className="px-5 py-4"><p className="text-sm font-medium text-white max-w-xs truncate">{t.title}</p></td>
                  {/* Customer/expert names link to their profile —
                      stopPropagation so clicking the name doesn't also
                      fire any future row-level click handler. Customer
                      links to /customers/:id (User.id); expert links to
                      /experts/:id (Expert.id) — see the id fields' notes
                      in types/index.ts's PaymentTransaction. */}
                  <td className="px-5 py-4 text-sm">
                    {t.customerId ? (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/customers/${t.customerId}`) }}
                        className="text-gray-300 hover:text-brand-400 hover:underline transition-colors text-left">
                        {t.customerName || '—'}
                      </button>
                    ) : (
                      <span className="text-gray-300">{t.customerName || '—'}</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {t.expertId ? (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/experts/${t.expertId}`) }}
                        className="text-gray-300 hover:text-brand-400 hover:underline transition-colors text-left">
                        {t.expertName || '—'}
                      </button>
                    ) : (
                      <span className="text-gray-600">Not hired</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">{t.finalPrice != null ? `$${t.finalPrice.toFixed(2)}` : '—'}</td>
                  {/* C/T = Customer / Tasker — the actual split per the
                      category's fee settings, not always 100/0. */}
                  <td className="px-5 py-4 text-xs text-gray-300">${(t.insuranceFeeCustomer ?? 0).toFixed(2)} / ${(t.insuranceFeeTasker ?? 0).toFixed(2)}</td>
                  <td className="px-5 py-4 text-xs text-gray-300">${(t.serviceFeeCustomer ?? 0).toFixed(2)} / ${(t.serviceFeeTasker ?? 0).toFixed(2)}</td>
                  <td className="px-5 py-4 text-xs text-gray-300">${(t.processingFeeCustomer ?? 0).toFixed(2)} / ${(t.processingFeeTasker ?? 0).toFixed(2)}</td>
                  <td className="px-5 py-4 text-xs text-gray-300">${(t.taxAmount ?? 0).toFixed(2)} / ${(t.taxFeeTasker ?? 0).toFixed(2)}</td>
                  <td className="px-5 py-4 text-sm text-white font-medium">{t.totalCharged != null ? `$${(t.customerPaid ?? t.totalCharged).toFixed(2)}` : '—'}</td>
                  {/* The four always add up: Customer Paid = Stripe + Tasker + Platform.
                      "est." = Stripe has not confirmed its exact fee yet. */}
                  <td className="px-5 py-4 text-sm text-blue-400">
                    ${(t.stripeFee ?? 0).toFixed(2)}
                    {t.stripeFeeIsFinal === false && <span className="text-xs text-gray-500"> est.</span>}
                    {t.stripeFeeIsFinal && (t.stripeFeeDiff ?? 0) !== 0 && (
                      <p className={`text-xs ${(t.stripeFeeDiff ?? 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {(t.stripeFeeDiff ?? 0) > 0 ? '+' : '−'}${Math.abs(t.stripeFeeDiff ?? 0).toFixed(2)} vs est. ${(t.stripeFeeEstimate ?? 0).toFixed(2)}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-green-400">
                    ${t.expertEarned.toFixed(2)}
                    {t.expertIsFinal === false && <span className="text-xs text-gray-500"> est.</span>}
                    {(t.taxOnJob ?? 0) > 0 && <p className="text-xs text-gray-500">incl. job tax ${t.taxOnJob.toFixed(2)} (tasker remits)</p>}
                    {t.taxJurisdictionLabel && <p className="text-xs text-gray-500">{t.taxJurisdictionLabel}{t.taxProvinceApplied ? ` · ${t.taxProvinceApplied}` : ''}{t.taxExpertRegisteredApplied != null ? ` · ${t.taxExpertRegisteredApplied ? 'registered' : 'not registered'}` : ''}{t.taxPricingModeApplied === 'TAX_INCLUDED' ? ` · tax included ($${Number(t.taxIncludedInJob || 0).toFixed(2)})` : t.taxPricingModeApplied === 'TAX_ADDED' ? ' · tax added' : ''}</p>}
                  </td>
                  <td className="px-5 py-4 text-sm text-brand-400">
                    ${(t.applicationFee ?? t.platformFee).toFixed(2)}
                    {t.taxCollected != null && (
                      <p className="text-xs text-gray-500">tax ${t.taxCollected.toFixed(2)} · keeps ${(t.platformKeeps ?? 0).toFixed(2)}</p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">${t.platformFee.toFixed(2)}</td>
                  <td className="px-5 py-4 text-sm text-gray-400">{t.hiredAt ? new Date(t.hiredAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  )
}

export default PaymentsPage